import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import GeoJSON from 'ol/format/GeoJSON';
import * as olExtent from 'ol/extent';

import {assetVersion} from '../config';

const Details = ({stand, isExpanded}) => {
	/** @type {React.RefObject<HTMLDivElement>} contentRef */
	const contentRef = useRef();
	/** @type {React.RefObject<HTMLImageElement>} imageRef */
	const imageRef = useRef();
	/** @type {React.RefObject<HTMLDivElement>} contentRef */
	const navButtonDivRef = useRef();

	const [navDialogVisible, setNavDialogVisible] = useState(false);
	if (navDialogVisible && navDialogVisible !== stand.id) {
		setNavDialogVisible(false);
	}

	useEffect(() => {
		if (!contentRef.current) {
			return;
		}

		contentRef.current.querySelectorAll('a[href]').forEach((link) => {
			link.target = '_blank';

			const href = link.getAttribute('href');
			if (href && href.startsWith('/')) {
				link.setAttribute('href', 'https://www.braunschweig.de' + href);
			}
		});
	}, [stand.description]);

	// If the stand changes, set image to "loading"
	useLayoutEffect(() => {
		if (imageRef.current) {
			imageRef.current.classList.add('bssp-details-image--loading');
		}
	}, [stand.id]);

	const handleImageError = useCallback((e) => {
		e.target.classList.add('bssp-details-image--error');
		e.target.src = `https://www.braunschweig.de/weihnachtsmarkt/standplan/fotos/_placeholder.jpg?v=${assetVersion}`;
	}, []);

	const handleImageLoaded = useCallback((e) => {
		e.target.classList.remove('bssp-details-image--error');
		e.target.classList.remove('bssp-details-image--loading');
	}, []);

	const handleShare = useCallback(
		(e) => {
			e.preventDefault();
			e.stopPropagation();

			if (navigator.share) {
				navigator
					.share({
						title: stand.name + ' - Weihnachtsmarkt Braunschweig',
						text: stand.name + ' - Weihnachtsmarkt Braunschweig',
						url: `https://www.braunschweig.de/apps/standplan/?stand=${stand.id}&mtm_campaign=wm24&mtm_source=share#${stand.id}`,
					})
					.then(() => {
						// ignore
					});
			}
		},
		[stand.id, stand.name]
	);

	const handleNavClick = useCallback(
		(e) => {
			e.preventDefault();
			e.stopPropagation();

			setNavDialogVisible(navDialogVisible ? false : stand.id);
		},
		[navDialogVisible, stand.id]
	);

	useEffect(() => {
		if (!imageRef.current) {
			return;
		}

		imageRef.current.classList.remove('bssp-details-image--error');
		imageRef.current.classList.add('bssp-details-image--loading');
	}, [stand.id]);

	useEffect(() => {
		document
			.querySelector('meta[property=og\\:title]')
			.setAttribute(
				'content',
				stand.name + ' - Weihnachtsmarkt Braunschweig'
			);
		document
			.querySelector('meta[property=og\\:description]')
			.setAttribute('content', stand.listInformation);
		document
			.querySelector('meta[property=og\\:url]')
			.setAttribute(
				'content',
				`https://www.braunschweig.de/apps/standplan/?stand=${encodeURIComponent(
					stand.id
				)}&mtm_campaign=wm24&mtm_source=og#${encodeURIComponent(
					stand.id
				)}`
			);
		document
			.querySelector('meta[property=og\\:image]')
			.setAttribute(
				'content',
				`https://www.braunschweig.de/apps/standplan/og.php?stand=${encodeURIComponent(
					stand.id
				)}`
			);
		document
			.querySelector('link[rel=canonical]')
			.setAttribute(
				'href',
				`https://www.braunschweig.de/apps/standplan/?stand=${encodeURIComponent(
					stand.id
				)}#${encodeURIComponent(stand.id)}`
			);
	}, [stand.id, stand.listInformation, stand.name]);

	useEffect(() => {
		if (!navButtonDivRef.current) {
			return () => undefined;
		}

		const handleClick = () => {
			setNavDialogVisible(false);
		};

		const el = navButtonDivRef.current;
		el.addEventListener('click', handleClick);
		return () => {
			el.removeEventListener('click', handleClick);
		};
	});

	const format = new GeoJSON();
	const centroid = olExtent.getCenter(
		format
			.readGeometry(stand.geometry, {
				dataProjection: 'EPSG:4326',
				featureProjection: 'EPSG:4326',
			})
			.getExtent()
	);

	const lat =
		centroid && String(Math.round(centroid[1] * 1_000_000) / 1_000_000);
	const lon =
		centroid && String(Math.round(centroid[0] * 1_000_000) / 1_000_000);

	return (
		<div className="bssp-details-container">
			<img
				ref={imageRef}
				src={`https://www.braunschweig.de/weihnachtsmarkt/standplan/fotos/${stand.id}.jpg?v=${assetVersion}`}
				alt=""
				className={
					'bssp-details-image' +
					(isExpanded ? ' bssp-details-image--big' : '')
				}
				onError={handleImageError}
				onLoad={handleImageLoaded}
			/>
			<h3 className="bssp-details-headline">{stand.name}</h3>

			<div
				ref={contentRef}
				dangerouslySetInnerHTML={{__html: stand.description}}
			/>

			<p>
				<em>Kategorie: {stand.category}</em>
			</p>

			<div className="bssp-actions">
				{centroid ? (
					<div className="bssp-action">
						<button
							className="bssp-action-button"
							type="button"
							aria-controls="bssp-nav-button-dialog"
							onClick={handleNavClick}
						>
							<svg
								className="bssp-action-button__icon"
								xmlns="http://www.w3.org/2000/svg"
								width="14"
								height="19"
								viewBox="0 0 14 19"
							>
								<path
									fill="#000"
									d="M13.17 9.82L7.35 19h-.7L.83 9.82h.03A6.28 6.28 0 010 6.68C0 2.98 3.13 0 7 0c1.86 0 3.64.7 4.95 1.95a6.34 6.34 0 011.2 7.87h.02zm-6.43 6.46h.5l3.48-6.49H3.33l3.41 6.49zm.29-14.36c-1.32 0-2.59.5-3.52 1.38a4.54 4.54 0 00-.17 6.5h7.38a4.54 4.54 0 00-.17-6.5 5.11 5.11 0 00-3.52-1.38zm0 6.7a2.07 2.07 0 01-1.84-.96 1.88 1.88 0 010-2 2.07 2.07 0 011.84-.95 2 2 0 012.05 1.95c0 .52-.22 1.02-.6 1.38-.39.37-.9.58-1.45.58z"
								/>
							</svg>
							<span className="bssp-action-button__label">
								Navigation
							</span>
						</button>
						<div
							className="bssp-action__dialog"
							id="bssp-nav-button-dialog"
							aria-hidden={
								navDialogVisible === stand.id ? 'false' : 'true'
							}
							ref={navButtonDivRef}
						>
							<a
								target="_blank"
								rel="nofollow noreferrer"
								href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
									`${lat},${lon}`
								)}&travelmode=walking`}
							>
								Google Maps
							</a>
							<a
								target="_blank"
								rel="nofollow noreferrer"
								href={`https://maps.apple.com/?q=${encodeURIComponent(
									stand.name
								)}&ll=${encodeURIComponent(
									`${lat},${lon}`
								)}&z=20&dirflg=w`}
							>
								Apple Maps
							</a>
							<a
								target="_blank"
								rel="nofollow noreferrer"
								href={`https://www.nunav.to/destination?latitude=${lat}&longitude=${lon}&label=${encodeURIComponent(
									stand.name
								)}`}
							>
								NUNAV
							</a>
						</div>
					</div>
				) : null}

				{navigator.share ? (
					<div className="bssp-action">
						<button
							type="button"
							className="bssp-action-button"
							onClick={handleShare}
						>
							<svg
								className="bssp-action-button__icon"
								xmlns="http://www.w3.org/2000/svg"
								width="20"
								height="22"
								viewBox="0 0 20 22"
							>
								<path
									fill="#000"
									d="M10.86.91l4.42 4.42-1.33 1.32L11 3v10H9V3L6.06 6.6 4.7 5.37 9.15.9 9.13.87l.84-.84.03.03.03-.03.85.86z"
								/>
								<path
									stroke="#000"
									strokeLinecap="square"
									strokeWidth="2"
									d="M1 21h18M1 21V9M19 21V9M14 9h5M1 9h5"
								/>
							</svg>
							<span className="bssp-action-button__label">
								Teilen
							</span>
						</button>
					</div>
				) : null}
			</div>
		</div>
	);
};

export default Details;
